import { useRef, useState, type DragEvent, type PointerEvent } from "react";
import type {
  AdminSchedulingAppointment,
  AdminSchedulingAppointmentMutationRequest,
  AdminSchedulingResource,
} from "./scheduling-portal-model";

export interface SchedulingPointerRescheduleControlsProps {
  readonly appointment: AdminSchedulingAppointment;
  readonly resources: readonly AdminSchedulingResource[];
  readonly disabled: boolean;
  readonly busy: boolean;
  readonly onMutationRequest: (
    request: AdminSchedulingAppointmentMutationRequest,
  ) => void;
}

const appointmentDragType = "application/x-whitehaven-appointment";
const resizeThresholdPixels = 28;

function draggedAppointmentId(event: DragEvent<HTMLElement>) {
  if (!event.dataTransfer) return "";
  return (
    event.dataTransfer.getData(appointmentDragType) ||
    event.dataTransfer.getData("text/plain")
  );
}

export function SchedulingPointerRescheduleControls({
  appointment,
  resources,
  disabled,
  busy,
  onMutationRequest,
}: SchedulingPointerRescheduleControlsProps) {
  const blocked = disabled || busy;
  const resizeStart = useRef<number | null>(null);
  const [resizeDelta, setResizeDelta] = useState<-15 | 15 | null>(null);
  const [dragging, setDragging] = useState(false);

  const requestTimeMove = (
    event: DragEvent<HTMLDivElement>,
    deltaMinutes: -15 | 15,
  ) => {
    event.preventDefault();
    if (blocked || draggedAppointmentId(event) !== appointment.appointmentId) {
      return;
    }
    onMutationRequest({
      kind: "MOVE_TIME",
      appointmentId: appointment.appointmentId,
      expectedRevision: appointment.revision,
      deltaMinutes,
    });
    setDragging(false);
  };

  const requestResourceMove = (
    event: DragEvent<HTMLDivElement>,
    resource: AdminSchedulingResource,
  ) => {
    event.preventDefault();
    if (
      blocked ||
      draggedAppointmentId(event) !== appointment.appointmentId ||
      appointment.resourceIds.includes(resource.id)
    ) {
      return;
    }
    onMutationRequest({
      kind: "MOVE_RESOURCE",
      appointmentId: appointment.appointmentId,
      expectedRevision: appointment.revision,
      resourceId: resource.id,
    });
    setDragging(false);
  };

  const endResize = (event: PointerEvent<HTMLDivElement>) => {
    if (resizeStart.current === null) return;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // The browser may have released capture after a pointer cancellation.
    }
    resizeStart.current = null;
    const requestedDelta = resizeDelta;
    setResizeDelta(null);
    if (!blocked && requestedDelta !== null) {
      onMutationRequest({
        kind: "RESIZE",
        appointmentId: appointment.appointmentId,
        expectedRevision: appointment.revision,
        deltaMinutes: requestedDelta,
      });
    }
  };

  return (
    <section className="scheduling-pointer-controls" aria-hidden="true">
      <p>
        Pointer controls: drag the appointment handle onto a time target, or
        drag the duration handle left or right. Keyboard controls remain below.
      </p>
      <div className="scheduling-pointer-controls__move">
        <div
          className="scheduling-pointer-controls__drag-handle"
          draggable={!blocked}
          data-disabled={blocked || undefined}
          onDragStart={(event) => {
            if (blocked || !event.dataTransfer) {
              event.preventDefault();
              return;
            }
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData(
              appointmentDragType,
              appointment.appointmentId,
            );
            event.dataTransfer.setData("text/plain", appointment.appointmentId);
            setDragging(true);
          }}
          onDragEnd={() => setDragging(false)}
        >
          Drag appointment
        </div>
        <div
          className="scheduling-pointer-controls__drop-target"
          data-drop-direction="earlier"
          onDragOver={(event) => {
            if (!blocked && event.dataTransfer) {
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
            }
          }}
          onDrop={(event) => requestTimeMove(event, -15)}
        >
          Drop 15 minutes earlier
        </div>
        <div
          className="scheduling-pointer-controls__drop-target"
          data-drop-direction="later"
          onDragOver={(event) => {
            if (!blocked) {
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
            }
          }}
          onDrop={(event) => requestTimeMove(event, 15)}
        >
          Drop 15 minutes later
        </div>
      </div>
      <div className="scheduling-pointer-controls__move">
        {resources
          .filter(
            (resource) =>
              resource.status === "ACTIVE" &&
              !appointment.resourceIds.includes(resource.id),
          )
          .map((resource) => (
            <div
              className="scheduling-pointer-controls__drop-target"
              data-drop-resource-id={resource.id}
              data-drop-resource-kind={resource.kind}
              key={resource.id}
              onDragOver={(event) => {
                if (!blocked && event.dataTransfer) {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }
              }}
              onDrop={(event) => requestResourceMove(event, resource)}
            >
              Drop on {resource.displayLabel} (
              {resource.kind.replaceAll("_", " ")})
            </div>
          ))}
      </div>
      <div
        className="scheduling-pointer-controls__resize-handle"
        data-disabled={blocked || undefined}
        onPointerDown={(event) => {
          if (blocked) return;
          resizeStart.current = event.clientX;
          setResizeDelta(null);
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (resizeStart.current === null || blocked) return;
          const distance = event.clientX - resizeStart.current;
          setResizeDelta(
            distance <= -resizeThresholdPixels
              ? -15
              : distance >= resizeThresholdPixels
                ? 15
                : null,
          );
        }}
        onPointerUp={endResize}
        onPointerCancel={(event) => {
          resizeStart.current = null;
          setResizeDelta(null);
          try {
            event.currentTarget.releasePointerCapture(event.pointerId);
          } catch {
            // Pointer capture may already be released.
          }
        }}
      >
        Drag duration handle
      </div>
      <p className="scheduling-pointer-controls__status">
        {dragging
          ? "Appointment selected for pointer drop."
          : resizeDelta === -15
            ? "Release to shorten by 15 minutes."
            : resizeDelta === 15
              ? "Release to lengthen by 15 minutes."
              : ""}
      </p>
    </section>
  );
}
